/**
 * Kalenderfarben aus einem frei gewählten Farbton.
 *
 * Luca, 16.09.2026: *„Ich möchte, dass man Farben für Leistungen und für
 * Vermietung noch mehr wählen kann, über einen Farbpicker … und das dann auch
 * speichern kann."*
 *
 * Bis dahin standen sechs geprüfte Paare zur Wahl, und der Grund dafür bleibt
 * richtig: Ein selbst gemischtes Grün trifft den Kontrast nicht, den Hell
 * **und** Dunkel beide brauchen. Die Lösung, die beides erfüllt:
 *
 * > **Der Farbton ist frei, Helligkeit und Sättigung sind geführt.**
 *
 * Aus einer gewählten Farbe wird nur der Farbton übernommen und — gedeckelt —
 * ihre Buntheit. Die Helligkeit jeder Rolle ist fest und an den sechs
 * geprüften Paaren abgelesen. Damit hält der Kontrast für **jeden** Farbton,
 * und keiner muss ihn nachmessen.
 *
 * **Warum OKLCH und nicht HSL.** In HSL haben Gelb und Blau bei derselben
 * „Helligkeit" 50 % eine völlig verschiedene wahrgenommene Helligkeit — ein
 * festes L dort hielte den Kontrast gerade nicht fest. OKLab ist so gebaut,
 * dass gleiches L gleich hell aussieht (Björn Ottosson, 2020). Die Umrechnung
 * steht unten ausgeschrieben, ohne Bibliothek: `fach` hat keine Abhängigkeiten.
 *
 * Die fünf Rollen, wie `brett.css` sie benutzt:
 *
 * - `fuell`   — die Fläche des Balkens im hellen Thema: sehr hell, fast unbunt
 * - `kante`   — die Kontur im hellen Thema: dunkel, am buntesten
 * - `text`    — die Schrift auf `fuell`: noch etwas dunkler als die Kante
 * - `dunkel`  — die Fläche im dunklen Thema: dunkel und blass
 * - `kante_d` — Kontur **und Schrift** im dunklen Thema: hell
 *
 * Reine Funktionen, keine Abhängigkeiten, kein Text — wie überall in `fach`.
 */
const HEX_MUSTER = /^#[0-9a-f]{6}$/i;
/** `#RRGGBB`, Groß- oder Kleinschreibung egal. Kein `#RGB`, kein Alpha. */
export function istHexfarbe(wert) {
    return HEX_MUSTER.test(wert);
}
// ---------------------------------------------------------------------------
// sRGB ↔ OKLab
// ---------------------------------------------------------------------------
/** sRGB-Kanal (0..1) → linear. Die Gammakurve aus IEC 61966-2-1. */
function linear(kanal) {
    return kanal <= 0.04045 ? kanal / 12.92 : ((kanal + 0.055) / 1.055) ** 2.4;
}
/** Linear → sRGB-Kanal (0..1). */
function gamma(kanal) {
    return kanal <= 0.0031308 ? kanal * 12.92 : 1.055 * kanal ** (1 / 2.4) - 0.055;
}
function hexZuLinear(hex) {
    if (!istHexfarbe(hex))
        throw new Error(`farben.hex: ${JSON.stringify(hex)}`);
    return [
        linear(parseInt(hex.slice(1, 3), 16) / 255),
        linear(parseInt(hex.slice(3, 5), 16) / 255),
        linear(parseInt(hex.slice(5, 7), 16) / 255),
    ];
}
function linearZuLab([r, g, b]) {
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [
        0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    ];
}
function labZuLinear([L, a, b]) {
    const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
    const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
    const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
    return [
        4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    ];
}
function hexZuLab(hex) {
    return linearZuLab(hexZuLinear(hex));
}
/** Liegt die Farbe im sRGB-Raum? Mit einer Toleranz für Rundungsreste. */
function imRaum(lin) {
    const TOLERANZ = 1e-6;
    return lin.every((k) => k >= -TOLERANZ && k <= 1 + TOLERANZ);
}
function linearZuHex(lin) {
    return ('#' +
        lin
            .map((k) => Math.round(Math.min(1, Math.max(0, gamma(Math.min(1, Math.max(0, k))))) * 255))
            .map((k) => k.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase());
}
// ---------------------------------------------------------------------------
// Die geführten Stufen
// ---------------------------------------------------------------------------
/**
 * Helligkeit (OKLab-L) und Buntheit je Rolle.
 *
 * **Abgelesen, nicht erfunden.** Die L-Werte sind die Mittel der sechs
 * geprüften Paare aus `apps/api/src/routen/farben.ts` (Stahl, Oliv, Ziegel,
 * Schiefer, Pflaume, Petrol), gerundet:
 *
 *   fuell 0,927–0,940 · kante 0,388–0,506 · text 0,334–0,427
 *   dunkel 0,307–0,343 · kante_d 0,760–0,814
 *
 * `anteil` ist die Buntheit relativ zur Kante, ebenfalls aus den Paaren: Die
 * Fläche hat etwa ein Viertel davon, die Schrift etwas weniger als die Kante,
 * die dunkle Fläche gut ein Drittel. `hoechstens` deckelt je Rolle — eine
 * Fläche, die leuchtet, ist keine Fläche mehr, sondern ein Signal.
 */
const STUFEN = {
    fuell: { L: 0.93, anteil: 0.25, hoechstens: 0.03 },
    kante: { L: 0.44, anteil: 1, hoechstens: 0.12 },
    text: { L: 0.37, anteil: 0.85, hoechstens: 0.1 },
    dunkel: { L: 0.33, anteil: 0.4, hoechstens: 0.045 },
    kante_d: { L: 0.78, anteil: 1.1, hoechstens: 0.13 },
};
/** Die Buntheit der Kante höchstens. Der Deckel über der gewählten Farbe. */
const BUNTHEIT_HOECHSTENS = STUFEN.kante.hoechstens;
/**
 * Eine Farbe aus Helligkeit, Buntheit und Farbton — und wenn sie so nicht in
 * sRGB liegt, mit so viel weniger Buntheit, bis sie es tut. Der Farbton bleibt
 * dabei stehen; das ist die Zusage an den, der ihn gewählt hat.
 *
 * Halbierungssuche statt einer Formel: Die Grenze des sRGB-Raums ist in OKLCH
 * keine geschlossene Kurve, und 24 Schritte sind genauer als 8 Bit je Kanal.
 */
function inDenRaum(L, C, h) {
    const lab = (c) => [L, c * Math.cos(h), c * Math.sin(h)];
    const voll = labZuLinear(lab(C));
    if (imRaum(voll))
        return linearZuHex(voll);
    let innen = 0;
    let aussen = C;
    for (let i = 0; i < 24; i += 1) {
        const mitte = (innen + aussen) / 2;
        if (imRaum(labZuLinear(lab(mitte))))
            innen = mitte;
        else
            aussen = mitte;
    }
    return linearZuHex(labZuLinear(lab(innen)));
}
/**
 * Aus einer gewählten Farbe das Paar für das Brett.
 *
 * Übernommen wird der **Farbton** und die **Buntheit bis zum Deckel**: Wer ein
 * gedecktes Blau wählt, bekommt ein gedecktes Paar, wer ein kräftiges wählt,
 * ein kräftiges — aber keines, das den Kontrast verliert. Die Helligkeit kommt
 * nie aus der Eingabe. Ein fast weißes Gelb ergibt deshalb dieselbe Kante wie
 * ein sattes Gelb desselben Tons, nur blasser.
 *
 * Grau, Schwarz und Weiß haben keinen Farbton; ihre Buntheit ist null, und der
 * Winkel spielt dann keine Rolle. Heraus kommt ein unbuntes Paar — Schiefer.
 */
export function farbpaarAus(hex) {
    const [, a, b] = hexZuLab(hex);
    const buntheit = Math.min(Math.hypot(a, b), BUNTHEIT_HOECHSTENS);
    // Unter einem Tausendstel ist der Winkel Rauschen aus der Rundung auf 8 Bit.
    const winkel = buntheit < 1e-3 ? 0 : Math.atan2(b, a);
    const unbunt = buntheit < 1e-3;
    const rolle = (r) => {
        const stufe = STUFEN[r];
        const C = unbunt ? 0 : Math.min(buntheit * stufe.anteil, stufe.hoechstens);
        return inDenRaum(stufe.L, C, winkel);
    };
    return {
        fuell: rolle('fuell'),
        kante: rolle('kante'),
        text: rolle('text'),
        dunkel: rolle('dunkel'),
        kante_d: rolle('kante_d'),
    };
}
/** Euklidischer Abstand zweier Farben in OKLab. 0 = gleich, ~0,02 = gerade sichtbar. */
export function farbabstand(a, b) {
    const [L1, a1, b1] = hexZuLab(a);
    const [L2, a2, b2] = hexZuLab(b);
    return Math.hypot(L1 - L2, a1 - a2, b1 - b2);
}
/**
 * Wie weit zwei Kanten auseinanderliegen, **nachdem beide auf dieselbe Stufe
 * gebracht sind**.
 *
 * Nicht einfach `farbabstand(kanteA, kanteB)`, und das ist der Punkt: Die
 * sechs geprüften Paare haben ihre Kanten von Hand gesetzt, zwischen L 0,39
 * und 0,51. Eine eigene Farbe bekommt immer L 0,44. Wer als eigene Farbe genau
 * die Kante von Stahl wählt, bekäme ein Paar, das neben Stahl **gleich
 * aussieht** — aber schon der Helligkeitsunterschied allein hielte die beiden
 * rechnerisch 0,05 auseinander. Beide Seiten durch `farbpaarAus` zu schicken
 * lässt nur übrig, was nach dem Führen noch unterscheidet: Farbton und
 * Buntheit.
 */
export function kantenabstand(a, b) {
    return farbabstand(farbpaarAus(a).kante, farbpaarAus(b).kante);
}
/**
 * Darunter gelten zwei Geschäftsfarben als **dieselbe** (`farben.doppelt`).
 *
 * Nachgerechnet in `test/farben.test.ts`: Die sechs geprüften Paare liegen
 * untereinander, nach `kantenabstand`, bei mindestens 0,039 (Stahl–Petrol;
 * Stahl–Schiefer 0,046) — und die dürfen nebeneinanderstehen. Die Schwelle
 * liegt mit Abstand darunter. Zwei satte Farben (Buntheit am Deckel) sind
 * damit ab etwa 14° Farbtonabstand verschieden; zwei fast graue sind es nie.
 *
 * 0,08, der erste Gedanke, hätte schon Stahl neben Petrol und Stahl neben
 * Schiefer abgewiesen — zwei Paare, die als geprüft nebeneinander erlaubt
 * sind. Eine Schwelle, die die Vorgaben selbst nicht bestehen, prüft etwas
 * anderes als „sieht gleich aus".
 */
export const FARBABSTAND_MINDESTENS = 0.03;
/** Relative Luminanz nach WCAG 2.x — für den Kontrast, nicht für die Führung. */
export function luminanz(hex) {
    const [r, g, b] = hexZuLinear(hex);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Kontrastverhältnis nach WCAG 2.x, 1 bis 21. */
export function kontrast(a, b) {
    const x = luminanz(a);
    const y = luminanz(b);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
//# sourceMappingURL=farben.js.map